import React from 'react';
import { Search, Filter, X, Calendar, Settings, Target, User, Activity, Clock, Gauge, ThumbsUp, ThumbsDown } from 'lucide-react';
import { DateRangePicker } from './date-range-picker';
import { MultiSelect } from './multi-select';
import { StatusFilter } from './StatusFilter';
import { Input } from './input';
import { Button } from './button';

interface TiltViewFiltersProps {
  // Search filters for sticky columns
  stationNameFilter: string;
  onStationNameFilterChange: (value: string) => void;
  bugTypeFilter: string[];
  onBugTypeFilterChange: (value: string[]) => void;
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  torchFilter: string[];
  onTorchFilterChange: (value: string[]) => void;
  passFilter: string[];
  onPassFilterChange: (value: string[]) => void;
  tiltRangeFilter: string[];
  onTiltRangeFilterChange: (value: string[]) => void;
  
  // Date range filter
  dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
  onDateRangeChange: (value: { dateFrom: Date | undefined; dateTo: Date | undefined }) => void;
  
  // Options for dropdowns
  bugTypeOptions: string[];
  statusOptions: string[];
  torchOptions: string[];
  passOptions: string[];
  tiltRangeOptions: string[];
  
  // New props for count display
  totalCount: number;
  filteredCount: number;
}

export const TiltViewFilters: React.FC<TiltViewFiltersProps> = ({
  stationNameFilter,
  onStationNameFilterChange,
  bugTypeFilter,
  onBugTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  torchFilter,
  onTorchFilterChange,
  passFilter,
  onPassFilterChange,
  tiltRangeFilter,
  onTiltRangeFilterChange,
  dateRange,
  onDateRangeChange,
  bugTypeOptions,
  statusOptions,
  torchOptions,
  passOptions,
  tiltRangeOptions,
  totalCount,
  filteredCount
}) => {
  const clearAllFilters = () => {
    onStationNameFilterChange('');
    onBugTypeFilterChange([]);
    onStatusFilterChange([]);
    onTorchFilterChange([]);
    onPassFilterChange([]);
    onTiltRangeFilterChange([]);
    onDateRangeChange({ dateFrom: undefined, dateTo: undefined });
  };

  const hasActiveFilters = stationNameFilter || bugTypeFilter.length > 0 || statusFilter.length > 0 || 
    torchFilter.length > 0 || passFilter.length > 0 || tiltRangeFilter.length > 0 || dateRange.dateFrom || dateRange.dateTo;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700 text-xs">Tilt View Filters</span>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear All
          </Button>
        )}
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Station Name Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Target className="h-4 w-4" />
            Station Name
          </label>
          <Input
            placeholder="Search station name..."
            value={stationNameFilter}
            onChange={(e) => onStationNameFilterChange(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Bug Type Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Bug Type
          </label>
          <MultiSelect
            options={bugTypeOptions.map(option => ({ value: option, label: option }))}
            selected={bugTypeFilter}
            onChange={onBugTypeFilterChange}
            placeholder="Select bug types"
          />
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Status
          </label>
          <StatusFilter
            selected={statusFilter}
            onChange={onStatusFilterChange}
            placeholder="Select status"
          />
        </div>

        {/* Torch Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Torch
          </label>
          <MultiSelect
            options={torchOptions.map(option => ({ value: option, label: option }))}
            selected={torchFilter}
            onChange={onTorchFilterChange}
            placeholder="Select torches"
          />
        </div>

        {/* Pass Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Target className="h-4 w-4" />
            Pass
          </label>
          <MultiSelect
            options={passOptions.map(option => ({ value: option, label: option }))}
            selected={passFilter}
            onChange={onPassFilterChange}
            placeholder="Select passes"
          />
        </div>

        {/* Tilt Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Target className="h-4 w-4" />
            Tilt Range
          </label>
          <MultiSelect
            options={tiltRangeOptions.map(option => ({ value: option, label: option }))}
            selected={tiltRangeFilter}
            onChange={onTiltRangeFilterChange}
            placeholder="Select tilt ranges"
          />
        </div>

                {/* Date Range Filter */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Date Range (Weld Start Time)
          </label>
          <DateRangePicker
            initialDateFrom={dateRange.dateFrom}
            initialDateTo={dateRange.dateTo}
            onUpdate={(values) => onDateRangeChange({
              dateFrom: values.dateFrom,
              dateTo: values.dateTo
            })}
            showCompare={false}
        className="w-[390px]"
          />
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {stationNameFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs rounded-full">
                Station: {stationNameFilter}
                <button
                  onClick={() => onStationNameFilterChange('')}
                  className="ml-1 hover:text-blue-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {bugTypeFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-galvanizing-green bg-[rgba(31,154,58,0.08)] text-xs rounded-full">
                Bug Type: {bugTypeFilter.join(', ')}
                <button
                  onClick={() => onBugTypeFilterChange([])}
                  className="ml-1 hover:text-green-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {statusFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs rounded-full">
                Status: {statusFilter.join(', ')}
                <button
                  onClick={() => onStatusFilterChange([])}
                  className="ml-1 hover:text-purple-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {torchFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[#B38600] bg-[rgba(230,161,0,0.08)] text-xs rounded-full">
                Torch: {torchFilter.join(', ')}
                <button
                  onClick={() => onTorchFilterChange([])}
                  className="ml-1 hover:text-orange-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {passFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] text-xs rounded-full">
                Pass: {passFilter.join(', ')}
                <button
                  onClick={() => onPassFilterChange([])}
                  className="ml-1 hover:text-indigo-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {tiltRangeFilter.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">
                Tilt Range: {tiltRangeFilter.join(', ')}
                <button
                  onClick={() => onTiltRangeFilterChange([])}
                  className="ml-1 hover:text-teal-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(dateRange.dateFrom || dateRange.dateTo) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-readable-red bg-[rgba(196,0,31,0.06)] text-xs rounded-full">
                Date Range: {dateRange.dateFrom ? `${dateRange.dateFrom.getDate().toString().padStart(2, '0')}-${(dateRange.dateFrom.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateFrom.getFullYear()} ${dateRange.dateFrom.getHours() % 12 || 12}:${dateRange.dateFrom.getMinutes().toString().padStart(2, '0')} ${dateRange.dateFrom.getHours() >= 12 ? 'PM' : 'AM'}` : ''} - {dateRange.dateTo ? `${dateRange.dateTo.getDate().toString().padStart(2, '0')}-${(dateRange.dateTo.getMonth() + 1).toString().padStart(2, '0')}-${dateRange.dateTo.getFullYear()} ${dateRange.dateTo.getHours() % 12 || 12}:${dateRange.dateTo.getHours() >= 12 ? 'PM' : 'AM'}` : ''}
                <button
                  onClick={() => onDateRangeChange({ dateFrom: undefined, dateTo: undefined })}
                  className="ml-1 hover:text-pink-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pagination Display */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Showing:</span>
            <span className="text-sm text-gray-900 font-semibold">
              {filteredCount > 0 ? `1-${filteredCount}` : '0'} of {totalCount}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}; 